import React, { useEffect, useState, useRef } from "react";
import { withSidebar } from "../hoc/withSidebar";
import { useTranslation } from "react-i18next";
import ERPSBaseTextField from "../custom/inputs/ERPSBaseTextField";
import ERPSBaseRadioGroup from "../custom/inputs/ERPSBaseRadioGroup";
import ERPSBaseCheckBoxGrp from "../custom/inputs/ERPSBaseCheckBoxGrp";
import ErrorMessage from "../custom/containers/ErrorMessage";
import ERPSBaseButton from "../custom/inputs/ERPSBaseButton";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import ConfirmationMessage from "../custom/containers/ConfirmationMesssage";
import actions from "../../actions";
import { useSelector, useDispatch } from "react-redux";
import userActionTypes from "../../actions/types/user.action.type";
import CustomModelDialog from "../custom/inputs/CustomModelDialog";
import {RoleType, COMPANY_ROLES, WEBPORTAL_ROLES, FI, Regex, YES_NO_LIST} from "../../constants/sharedConstants";
import {ResponsiveBox, Grid, Stack, Typography, HStack, Box} from "../primitives";
import {checkLengthAndRegex} from "../../utils/helper";

/**
 * Normalizes newUserReducer.error (from getExceptionMessage in user.service) for ErrorMessage.
 * Possible types: string | object (timestamp, errorMessage?, message?) | null | undefined.
 * Type	When
    string	403/404/401/400/504 messages, or 500 with string body, or errorRes.message, or result of getMessageByCode()
    object	When error.response.data has timestamp and showDetails and getMessageByCode() is null → full API error object (e.g. timestamp, traceId, errorMessage or message)
    null/undefined	Initial state or when no branch in getExceptionMessage sets a value
 */
function normalizeApiErrorForDisplay(apiCallError) {
  if (apiCallError == null) return null;
  if (typeof apiCallError === "string") return apiCallError;
  if (typeof apiCallError === "object") {
    if (apiCallError.errorMessage != null) return apiCallError;
    if (apiCallError.message != null) return { ...apiCallError, errorMessage: apiCallError.message };
    return apiCallError;
  }
  return null;
}

const NewUser = (props) => {
  const { t } = useTranslation();
  const pageUrl = props?.location?.pathname;
  const userRoleTypes = useSelector(
    (state) => state.userRoleTypeReducer?.userRoleTypeList
  );
  const subscribingEntitiesList = useSelector(
    (state) => state.subscribingEntitiesReducer?.subscribingEntitiesList
  );
  const successMessage = useSelector(
    (state) => state.newUserReducer?.newUserAdded
  );
  const loginUser = useSelector((state) => state.loginUserReducer?.user);
  const apiCallError = useSelector(
    (state) => state.newUserReducer?.error
  );
  const selectedFiNumber = useSelector((state) => state.fiReducer?.selectedFi);

  const selectedFi = useSelector((state) => state.fiReducer);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [error, setError] = useState([]);
  const [subscribeEntityList, setSubscribeEntityList] = useState([]);
  const [userRoleTypeList, setUserRoleTypeList] = useState([]);
  const [loader, setLoader] = useState(false);
  const dispatch = useDispatch();
  const errorRef = useRef(null);
  const successRef = useRef(null);
  const fiNumber = selectedFiNumber || loginUser?.fiNumber;

  const [openCancelDialog, setOpenCancelDialog] = useState(false);

  const [isUserIdDisabled, setUserIdDisabled] = useState(false);

  const [emailAddressError, setEmailAddressError] = useState("");

  const [fiData, setFiData] = useState(null);

  useEffect(() => {
    if (successRef.current) {
      successRef.current.focus();
    }
    setUserIdDisabled(false);
  }, [successMessage && showSuccessMessage]);

  useEffect(() => {
    if (subscribingEntitiesList) {
      let data = [];
      if (loginUser?.fiCompanyNumber?.trim() !== "") {
        subscribingEntitiesList.forEach(
          ({ fiCompanyNumber, fiCompanyName }) => {
            if (fiCompanyNumber === loginUser?.fiCompanyNumber) {
              data.push({
                value: fiCompanyNumber,
                label: fiCompanyName,
              });
            }
          }
        );
      } else {
        data = subscribingEntitiesList.map(
          ({ fiCompanyNumber, fiCompanyName }) => ({
            value: fiCompanyNumber,
            label: fiCompanyName,
          })
        );
        data.unshift({
          value: "",
          label: "",
        });
      }
      setSubscribeEntityList(data);
    }
  }, [subscribingEntitiesList]);

  useEffect(() => {
    setFiData(selectedFi?.financialInstitutionList?.find(
        ({ fi_number }) => fi_number.toString() === fiNumber
    ) || loginUser?.fi)
    if (apiCallError) {
      dispatch({ type: userActionTypes.NEW_USER_REQUEST });
    }
  }, []);

  useEffect(() => {
    if (userRoleTypes) {
      const data = userRoleTypes.map(({ role_id, role_type, role_name }) => ({
        roleId: role_id,
        value: role_id,
        label: role_name?.toUpperCase(),
      }));
      data.unshift({ roleId: "", value: "", label: "" });
      setUserRoleTypeList(data);
    }
  }, [userRoleTypes]);

  const {
    location: {
      state: { componentAccess },
    },
    history,
  } = props;

  const newUserComponent = componentAccess.filter(
    ({ componentType, childComponents }) => componentType === "NU"
  );

  const newUserComponentAccess = newUserComponent[0].childComponents;

  const languageItems = [
    { name: t("erps.common.label.english"), value: "E" },
    { name: t("erps.common.label.french"), value: "F" },
  ];

  const returnsAccessList = YES_NO_LIST.map(({ label, value }) =>
      ({label: t(label), value,}));

  const checkBillerUser = () => {
    return loginUser?.billerNonVolatileDto?.biller_id;
  };

  const checkBillPayPortal = () => {
    return localStorage.getItem("type") === "billpay";
  };

  const isBillpayPortalUser = (roleId) => {
    const isBillPayUser =  WEBPORTAL_ROLES.includes(parseInt(roleId))
         && (fiNumber === 777 || fiData?.owning_fi_number === 50 && fiNumber != 99);
    setUserIdDisabled(isBillPayUser);
    return isBillPayUser;
  };

  const checkEmailErrorMessage = (errorMessage) => {
    if (typeof errorMessage !== 'string') {
      return false;
    }
    const keywords = ["email address", "adresse e-mail"];
    return keywords.some(keyword => errorMessage?.includes(keyword));
  };


  const [values, setValues] = useState({
    userId: "",
    fiNumber,
    roleId:
      loginUser?.role?.role_id == 9 ? loginUser?.role?.role_id.toString() : "",
    languageCode: "E",
    fiCompanyNumber:
      loginUser?.fiCompanyNumber?.trim() !== ""
        ? loginUser?.fiCompanyNumber
        : "",
    firstName: "",
    lastName: "",
    emailAddress: "",
    phone: "",
    challengeNumber: 0,
    challengeResponse: "",
    changePasswordIndex: "Y",
    logonFailures: 0,
    emailNotification: "N",
    passwordHash: "",
    userIdErrorHighlight: false,
    roleIdErrorHighlight: false,
    subscribingEntityErrorHighlight: false,
    firstNameErrorHighlight: false,
    lastNameErrorHighlight: false,
    emailAddressErrorHighlight: false,
    phoneNumberErrorHighlight: false,
  });

  const handleChange = (prop) => (event) => {
    if (apiCallError && (prop === "userId" || prop === "emailAddress")) {
      dispatch({ type: userActionTypes.NEW_USER_REQUEST, error: null });
    }
    if (prop === "emailAddress") {
      const value = event.target.value;
      let errMsg = checkLengthAndRegex(value, "erps.newuser.label.emailaddress", 80, Regex.emailText);
      setEmailAddressError(errMsg);
      if ("" === errMsg)
        setValues({ ...values, [prop]: value });
    } else if (prop === "emailNotification") {
      setValues({ ...values, [prop]: (event.target.checked ? "Y" : "N") });
    } else if (prop === "roleId") {
      values.userIdErrorHighlight = false;
      setError([]);
      var newValues = { ...values, userId: isBillpayPortalUser(event.target.value) ? '' : values.userId ,  [prop]: event.target.value };

      // Set default value of emailNotification checkbox when roleId is changed
      if (pageUrl == "/manage-users/new-user" && loginUser?.role?.role_id == RoleType.BILLER_ADMIN.id) {
        if (loginUser?.billerNonVolatileDto?.email_notification_ok == "N" || event.target.value == RoleType.BILLER_USER.id)
          newValues.emailNotification = "N";
        else if (loginUser?.billerNonVolatileDto?.email_notification_ok == "Y" && event.target.value == RoleType.BILLER_ADMIN.id)
          newValues.emailNotification = "Y";
      }
      setValues(newValues);
    } else {
      setValues({ ...values, [prop]: event.target.value });
    }
  };

  const addUser = () => {
    dispatch({
      type: userActionTypes.NEW_USER_REQUEST,
      error: null,
      newUserAdded: null,
    });
    const errormsg = validateUser();
    setError(errormsg);

    if (!errormsg.length) {
      setLoader(true);
      setShowSuccessMessage(false);

      const finalObject = createUserFinalObjectForSave();
      const billerId = loginUser?.billerNonVolatileDto?.biller_id;

      dispatch(
        actions.userActions.saveUser(
          { ...finalObject, billerId, requestFrom: "manage-users" },
          setValues,
          {
            ...props,
            setLoader,
            loader,
            setShowSuccessMessage
          },
          userRoleTypeList,
          subscribingEntitiesList
        )
      );
    }
  };

  function createUserFinalObjectForSave() {
    const finalObject = { ...values };
    userRoleTypeList.forEach((userType) => {
      if (userType.value === values.roleId) {
        finalObject.roleId = userType.roleId;
      }
    });
    finalObject.billpayPortalUser = isBillpayPortalUser(finalObject.roleId)
    finalObject.actionCode = "A";
    finalObject.statusCode = "A";
    finalObject.loggedInUserId = loginUser.userId;
    return finalObject;
  }

  const validateEmail = (email) => {
    return Regex.email.test(email);
  };

  const checkUserIdLength = (userId) => {
    if (fiNumber == 2) {
      return userId.length < 6 || userId.length > 8
        ? t("erps.manageusers.length.userid.msg.fispecific.bns")
        : "";
    } else {
      return userId.length < 8 || userId.length > 10
        ? t("erps.manageusers.length.userid.msg.fispecific.others")
        : "";
    }
  };

  const checkUserIdFormat = (userId) => {
    return userId.toUpperCase().startsWith(fiNumber+fiData.fi_code_name);
  };

  const showSubscribingEntity = () => {
      return (fiNumber == FI.TFX && COMPANY_ROLES.includes(Number(values.roleId))) ||
          (fiNumber == FI.BMO && RoleType.COMPANY_USER.id == values.roleId);

  };

  function validateUser() {
    let errormsg = [];
    var alphaNumericRegEx = /^[0-9A-Za-z]+$/;
    var alphaNumericRegExWithSpace = /^[a-zA-Z0-9.\-' À-ÿ]+$/;
    var numericRegEx = /^[0-9]+$/;
    values.userIdErrorHighlight = false;
    values.roleIdErrorHighlight = false;
    values.subscribingEntityErrorHighlight = false;
    values.firstNameErrorHighlight = false;
    values.lastNameErrorHighlight = false;
    values.emailAddressErrorHighlight = false;
    values.phoneNumberErrorHighlight = false;

    if (!checkBillerUser() && !isBillpayPortalUser(values.roleId)) {
      if (!values.userId) {
        values.userIdErrorHighlight = true;
        errormsg.push(
          t("erps.manageusers.blank.userid.msg", "UserId cannot be blank.")
        );
      } else if (
        values.userId &&
        values.userId.match(alphaNumericRegEx) === null
      ) {
        values.userIdErrorHighlight = true;
        errormsg.push(
          t(
            "erps.manageusers.alphanumeric.userid.msg",
            "UserId must be alpha numeric."
          )
        );
      } else if (values.userId && checkUserIdLength(values.userId)) {
        values.userIdErrorHighlight = true;
        errormsg.push(t(checkUserIdLength(values.userId)));
      } else if (values.userId &&
          checkUserIdFormat(values.userId)
      ) {
        values.userIdErrorHighlight = true;
        errormsg.push(
            t("erps.manageusers.format.userid.msg", {
              userIdFormat: fiNumber+fiData.fi_code_name,
            })
        );
      }
    }

    if (!values.roleId) {
      values.roleIdErrorHighlight = true;
      errormsg.push(
        t("erps.manageusers.blank.roleId.msg", "Please select the Role.")
      );
    }

    if (!values.firstName) {
      values.firstNameErrorHighlight = true;
      errormsg.push(
        t("erps.manageusers.blank.firstname.msg", "First name cannot be blank.")
      );
    }
    if (
      values.firstName &&
      (values.firstName.trim().length < 1 ||
        values.firstName.trim().length > 25)
    ) {
      values.firstNameErrorHighlight = true;
      errormsg.push(
        t(
          "erps.manageusers.length.firstname.msg",
          "First name length is between 1 and 25 characters."
        )
      );
    }
    if (
      values.firstName &&
      values.firstName.match(alphaNumericRegExWithSpace) === null
    ) {
      values.firstNameErrorHighlight = true;
      errormsg.push(
        t(
          "erps.manageusers.alphanumeric.firstname.msg",
          "First name must be alpha numeric."
        )
      );
    }

    if (!values.lastName) {
      values.lastNameErrorHighlight = true;
      errormsg.push(
        t("erps.manageusers.blank.lastname.msg", "Last name cannot be blank.")
      );
    }
    if (
      values.lastName &&
      (values.lastName.trim().length < 1 || values.lastName.trim().length > 25)
    ) {
      values.lastNameErrorHighlight = true;
      errormsg.push(
        t(
          "erps.manageusers.length.lastname.msg",
          "Last name length is between 1 and 25 characters."
        )
      );
    }
    if (
      values.lastName &&
      values.lastName.match(alphaNumericRegExWithSpace) === null
    ) {
      values.lastNameErrorHighlight = true;
      errormsg.push(
        t(
          "erps.manageusers.alphanumeric.lastname.msg",
          "Last name  must be alpha numeric."
        )
      );
    }
    if (!values.emailAddress) {
      values.emailAddressErrorHighlight = true;
      errormsg.push(
        t(
          "erps.manageusers.blank.emailaddress.msg",
          "Email address cannot be blank."
        )
      );
    }
    if (values.emailAddress && !validateEmail(values.emailAddress)) {
      values.emailAddressErrorHighlight = true;
      errormsg.push(
        t(
          "erps.manageusers.format.emailaddress.msg",
          "Please enter your e-mail address in this format 'name@example.com'"
        )
      );
    }
    if (values.phone && values.phone.trim().length !== 10) {
      values.phoneNumberErrorHighlight = true;
      errormsg.push(
        t("erps.manageusers.length.phone.msg", "Phone length should be 10.")
      );
    }
    if (values.phone && values.phone.match(numericRegEx) === null) {
      values.phoneNumberErrorHighlight = true;
      errormsg.push(
        t("erps.manageusers.numeric.phone.msg", "Phone Must be numeric.")
      );
    }

    if (showSubscribingEntity() && !values.fiCompanyNumber) {
      errormsg.push(t("erps.manageusers.subscribingentity.msg"));
    }

    return errormsg;
  }

  const handleOpenCancelDialog = () => {
    setOpenCancelDialog(true);
  };

  const handleCloseCancelDialog = () => {
    setOpenCancelDialog(false);
  };

  const handleRedirect = () => {
    dispatch({
      type: userActionTypes.RESET_ERROR,
      error: null,
      newUserAdded: null,
    });
    history.push("/manage-users", { componentAccess });
  };

  const getWpBillerId = () => {
    if (fiNumber == 99) {
      return (
        loginUser?.searchBillerFilesDto?.fi_biller_with_biller_id ||
        loginUser?.billerNonVolatileDto?.biller_id
      );
    }
    else if (fiNumber == FI.CTB_BILLER && loginUser.billerCrossReferenceDto)
      return loginUser.billerCrossReferenceDto.centralizedBillerId;
    const externalBillerId =
      loginUser?.billerNonVolatileDto?.biller_fi_number || "";
    const internalBillerId = loginUser?.billerNonVolatileDto?.biller_id || "";
    return externalBillerId
      ? `${externalBillerId}-${internalBillerId}`
      : internalBillerId;
  };

  return (
    <div role="main">
      {successMessage && showSuccessMessage && (
        <ConfirmationMessage
          message={successMessage}
          setShowMessage={setShowSuccessMessage}
          inputRef={successRef}
        />
      )}
      <ResponsiveBox ml={3} width="auto">
        <Breadcrumb {...props} componentAccess={componentAccess} />
        <ResponsiveBox className="manageUsers__page__title">
          <Typography variant="h1">
            {t("erps.newuser.page.title", "Manage users")}
          </Typography>
        </ResponsiveBox>
        <ResponsiveBox mt={4}>
          <Typography variant="h2">
            {t("erps.newuser.account.settings", "Account Settings")}
          </Typography>
        </ResponsiveBox>
        <ResponsiveBox sx={{ mt: 0 }}>
          <Typography variant="body1">
            <Box component="span" className="MuiFormLabel-asterisk">*</Box>
            {" "}{t("erps.common.label.required.information")}
          </Typography>
        </ResponsiveBox>
        <ErrorMessage
          error={(() => {
            const normalizedApi = normalizeApiErrorForDisplay(apiCallError);
            const resolvedError =
              error?.length > 0
                ? (normalizedApi ? [...error, normalizedApi] : error)
                : normalizedApi;
            return resolvedError;
          })()}
          errorRef={errorRef}
          mt={2}
        />

        <ResponsiveBox component="form" autoComplete="off">
          {loginUser?.role?.role_id == 3 &&
            pageUrl == "/manage-users/new-user" && (
              <HStack spacing={3}>
                <div style={{ width: "210px", padding: "20px 0px 0px 0px" }}>
                  <Typography variant="body1">
                    {t("erps.billermanagement.label.billerId")}
                    <br />
                  </Typography>
                  <label className="test-label">{getWpBillerId()}</label>
                </div>
              </HStack>
            )}
          <HStack spacing={3}>
            <ERPSBaseTextField
              id="role"
              type="text"
              error={values.roleIdErrorHighlight}
              select={true}
              label={t("erps.newuser.label.role", "Role")}
              className="three_column__textField"
              value={values.roleId}
              onChange={handleChange("roleId")}
              size="small"
              items={userRoleTypeList}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: values.roleId ? true : false }}
              actionType="hasRoleDropDown"
              componentAccess={newUserComponentAccess}
              required={"true"}
              isDisabled={loginUser?.role?.role_id == 9 ? true : false}
            />
            {!checkBillerUser() && !checkBillPayPortal() && (
                <HStack spacing={3}>
                  <ERPSBaseTextField
                      id="userId"
                      type="text"
                      error={values.userIdErrorHighlight}
                      label={t("erps.manageusers.label.userid", "User ID")}
                      className="three_column__textField"
                      onChange={handleChange("userId")}
                      value={values.userId}
                      size="small"
                      required="true"
                      actionType="hasUserIDTextField"
                      isDisabled={isUserIdDisabled}
                      componentAccess={newUserComponentAccess}
                  />
                </HStack>
            )}
            {values.roleId == RoleType.BILLER_USER.id  &&
                loginUser.commonConfig?.IS_RETURNS_PORTAL_LIVE === "Y" && (
                <ERPSBaseTextField
                    id="returnsAccess"
                    type="text"
                    select={true}
                    label={t(
                        "erps.newuser.label.returns.access",
                        "Access to Returns Services"
                    )}
                    className="three_column__textField"
                    size="small"
                    value={values.returnsAccess}
                    items={returnsAccessList}
                    onChange={handleChange("returnsAccess")}
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                    required={"true"}
                    defaultValue={"N"}
                    resetWhenRender={true}
                />
            )}
            {showSubscribingEntity()
                && (
              <ERPSBaseTextField
                id="subscribeEntityId"
                select={true}
                type="text"
                label={t(
                  "erps.newuser.label.subscribingentityid",
                  "Subscribing entity id"
                )}
                className="three_column__textField"
                size="small"
                value={values.fiCompanyNumber}
                items={subscribeEntityList}
                onChange={handleChange("fiCompanyNumber")}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                isDisabled={loginUser?.fiCompanyNumber.trim() !== ""}
              />
            )}
          </HStack>
          <HStack spacing={3}>
            <ERPSBaseTextField
              id="firstname"
              type="text"
              error={values.firstNameErrorHighlight}
              label={t("erps.newuser.label.firstname", "First name")}
              className="three_column__textField"
              size="small"
              value={values.firstName}
              onChange={handleChange("firstName")}
              required="true"
              actionType="hasFNameTextField"
              componentAccess={newUserComponentAccess}
            />
            <ERPSBaseTextField
              id="lastName"
              type="text"
              error={values.lastNameErrorHighlight}
              label={t("erps.newuser.label.lastname", "Last name")}
              className="three_column__textField"
              size="small"
              value={values.lastName}
              onChange={handleChange("lastName")}
              required="true"
              actionType="hasLNameTextField"
              componentAccess={newUserComponentAccess}
            />
          </HStack>
          <HStack spacing={3}>
            <ERPSBaseTextField
              id="emailAddress"
              type="text"
              label={t("erps.newuser.label.emailaddress", "Email Address")}
              className="three_column__textField"
              size="small"
              value={values.emailAddress}
              onChange={handleChange("emailAddress")}
              actionType="hasEmailTextField"
              required="true"
              componentAccess={newUserComponentAccess}
              error={values.emailAddressErrorHighlight || checkEmailErrorMessage(apiCallError)|| !!emailAddressError}
              helperText={emailAddressError}
              onBlur={() => {
                setEmailAddressError("");
              }}
            />
            <ERPSBaseTextField
              id="phone"
              type="text"
              error={values.phoneNumberErrorHighlight}
              label={t("erps.newuser.label.phonenumber", "Phone number")}
              className="three_column__textField"
              size="small"
              onChange={handleChange("phone")}
              value={values.phone}
              actionType="hasPhoneTextField"
              componentAccess={newUserComponentAccess}
            />
          </HStack>

          <Grid container>
            <Grid item sm={6} lg={6}>
              <ERPSBaseRadioGroup
                name="language"
                value={values.languageCode}
                label={t("erps.common.label.language")}
                onChange={handleChange("languageCode")}
                items={languageItems}
                actionType="hasLangDropDown"
                componentAccess={newUserComponentAccess}
              />
            </Grid>
          </Grid>

          {loginUser?.role?.role_id == 3 && fiNumber != FI.CTB_BILLER
            && pageUrl == "/manage-users/new-user" && (
              <HStack spacing={3} mt={3}>
                <ERPSBaseCheckBoxGrp
                  name="email_notification"
                  value={values?.emailNotification === "Y"}
                  disabled={
                    checkBillerUser() &&
                    loginUser?.billerNonVolatileDto?.email_notification_ok == "N" &&
                    values.roleId == RoleType.BILLER_USER.id  // role 4
                  }
                  label={t("erps.billerregistration.label.sendNotification")}
                  onChange={handleChange("emailNotification")}
                />
              </HStack>
            )}

          <HStack spacing={2}>
            <ERPSBaseButton
              variant="secondary"
              actionType="hasCancelBtn"
              componentAccess={newUserComponentAccess}
              className="button_with_margin48"
              label={t("erps.common.button.cancel", "Cancel")}
              onClick={handleOpenCancelDialog}
            />
            <ERPSBaseButton
              actionType="hasSaveUserAccess"
              functionType="NU"
              variant="primary"
              onClick={addUser}
              className="button_with_margin48"
              label={t("erps.newuser.button.save", "Save")}
              loading={loader}
              componentAccess={newUserComponentAccess}
            />
          </HStack>
        </ResponsiveBox>
      </ResponsiveBox>
      <CustomModelDialog
        handleClose={handleCloseCancelDialog}
        open={openCancelDialog}
        dialogHeaderClassName={"dialogHeaderClassName"}
        dialogActionsClassName={"leftAlignDialogActions"}
        handleRedirect={handleRedirect}
      />
    </div>
  );
};

export default withSidebar(NewUser);
